import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { desktopMarkup } from "./markup.desktop";
import { mobileMarkup } from "./markup.mobile";

// The customer (party-realm) app lives on its own origin; "Giriş yap" leaves the public
// site for it. Overridable per-env; dev default matches apps/customer's vite port (5175).
const CUSTOMER_URL = import.meta.env.VITE_CUSTOMER_URL ?? "http://localhost:5175";

// Product-card title (as rendered in the founder's markup) → başvuru slug. Both the
// desktop long form ("Sağlık Sigortası") and the mobile short form ("Sağlık") resolve
// to the same product.
const PRODUCT_SLUGS: Record<string, string> = {
  "Trafik Sigortası": "trafik",
  Kasko: "kasko",
  "Sağlık Sigortası": "saglik",
  Sağlık: "saglik",
  DASK: "dask",
  "Seyahat Sigortası": "seyahat",
  Seyahat: "seyahat",
};

// Header marketing-nav label → the on-page section heading it should smooth-scroll to.
// Only labels with a REAL section land here; anything without one is neutralized (below)
// so it stops looking clickable-but-dead. Match by a distinctive heading substring.
const NAV_TARGETS: Record<string, string> = {
  "Nasıl çalışır": "Koruma, dört sakin adımda",
  "Hasar desteği": "Bir şey ters gittiğinde",
  Danışmanlar: "Hanenizle ilgilenecek danışmanla",
};
// Header-nav labels with NO matching section → made non-interactive (no dead link).
const NAV_NEUTRAL = new Set(["Güvenlik"]);

function pointerAncestor(el: Element, root: Element): HTMLElement | null {
  let node: HTMLElement | null = el as HTMLElement;
  while (node && node !== root) {
    if (node.style?.cursor === "pointer") return node;
    node = node.parentElement;
  }
  return null;
}

// The founder's export is pure presentational HTML with no anchors/handlers. Rather than
// hand-edit that pixel-source markup, we render it verbatim and wire the CTAs here by DOM
// delegation once, after mount. Guarded by data-csa-bound so StrictMode's double-invoke
// (and the desktop/mobile duplication) never double-binds.
function wireCtas(root: HTMLElement, go: (to: string) => void) {
  const bind = (el: Element, handler: () => void) => {
    const node = el as HTMLElement;
    if (node.dataset.csaBound) return;
    node.dataset.csaBound = "1";
    node.style.cursor = "pointer";
    node.addEventListener("click", (e) => {
      e.preventDefault();
      handler();
    });
  };

  // Smooth-scroll from a nav label to its section heading, scoped to the label's own
  // markup copy (.csa-desktop / .csa-mobile) so the visible copy is the one that scrolls.
  const scrollToHeading = (from: HTMLElement, headingText: string) => {
    const container = from.closest<HTMLElement>(".csa-desktop, .csa-mobile") ?? root;
    const target = Array.from(container.querySelectorAll("h1, h2, h3")).find((h) =>
      (h.textContent ?? "").includes(headingText),
    );
    // jsdom has no scrollIntoView — optional-chain so tests don't throw.
    (target as HTMLElement | undefined)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };
  // Strip a dead affordance without touching the founder's markup structure.
  const neutralize = (el: HTMLElement) => {
    if (el.dataset.csaBound) return;
    el.dataset.csaBound = "1";
    el.style.cursor = "default";
  };

  const leaves = Array.from(root.querySelectorAll<HTMLElement>("*")).filter(
    (n) => n.children.length === 0,
  );

  // Product cards → /basvuru/:urun. Match a leaf title, climb to its clickable card.
  for (const leaf of leaves) {
    const slug = PRODUCT_SLUGS[(leaf.textContent ?? "").trim()];
    if (!slug) continue;
    const card = pointerAncestor(leaf, root);
    if (card) bind(card, () => go(`/basvuru/${slug}`));
  }

  // "Giriş yap" (desktop) / "Giriş" (mobile) → the customer app on its own origin.
  // Next to it we INJECT a "Kayıt ol" pill → the standalone self-serve /kayit page
  // (SYSTEM 1). Injection (not a markup edit) keeps the founder's pixel-source
  // export verbatim; data-csa-kayit guards StrictMode's double-invoke so the pill
  // is minted once. Rendered as the primary of the pair (filled) next to the plain
  // "Giriş yap" text.
  for (const leaf of leaves) {
    const text = (leaf.textContent ?? "").trim();
    if (text === "Giriş yap" || text === "Giriş") {
      bind(leaf, () => {
        window.location.href = CUSTOMER_URL;
      });
      if (!leaf.dataset.csaKayit && leaf.parentElement) {
        leaf.dataset.csaKayit = "1";
        const pill = document.createElement("span");
        pill.textContent = "Kayıt ol";
        pill.style.cssText =
          "font-size: 13px; font-weight: 600; color: rgb(255, 255, 255); " +
          "background: rgb(0, 36, 158); border-radius: 8px; padding: 6px 12px; " +
          "cursor: pointer; white-space: nowrap;";
        leaf.parentElement.insertBefore(pill, leaf.nextSibling);
        bind(pill, () => go("/kayit"));
      }
    }
  }

  // Header marketing nav (desktop) — the founder's dead cursor:pointer spans. Wire the
  // ones backed by a real on-page section to smooth-scroll; neutralize the rest so nothing
  // looks clickable-but-dead. SPAN + cursor:pointer isolates the header nav (line 12) from
  // the product cards, language toggle and footer (footer spans carry no pointer). Handler/
  // cursor only — the founder's pixel-source markup is preserved verbatim.
  for (const leaf of leaves) {
    if (leaf.tagName !== "SPAN" || leaf.style.cursor !== "pointer") continue;
    const text = (leaf.textContent ?? "").trim();
    const heading = NAV_TARGETS[text];
    if (heading) {
      bind(leaf, () => scrollToHeading(leaf, heading));
    } else if (NAV_NEUTRAL.has(text)) {
      neutralize(leaf);
    }
  }

  // Explicit product CTA button in the mobile quote block.
  for (const btn of Array.from(root.querySelectorAll<HTMLButtonElement>("button"))) {
    const text = (btn.textContent ?? "").trim();
    if (text.startsWith("Trafik teklifi al")) {
      bind(btn, () => go("/basvuru/trafik"));
    } else if (/eşleş|görüş|danışmanlık/i.test(text)) {
      // Advisor-match CTAs (Danışmanla görüşün / eşleşin / Ücretsiz danışmanlık). There is
      // no advisor-match flow; the truest "start here" conversion is the quote funnel, so
      // these repoint to the flagship trafik başvuru (was the dead /danisman "Faz 2" stub).
      bind(btn, () => go("/basvuru/trafik"));
    } else if (text === "Nasıl çalışır") {
      // Dead hero "Nasıl çalışır" button → scroll to the how-it-works section.
      bind(btn, () => scrollToHeading(btn, "Koruma, dört sakin adımda"));
    }
  }
}

export function Homepage() {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) wireCtas(ref.current, navigate);
  }, [navigate]);

  return (
    <div ref={ref}>
      <div className="csa-desktop" dangerouslySetInnerHTML={{ __html: desktopMarkup }} />
      <div className="csa-mobile" dangerouslySetInnerHTML={{ __html: mobileMarkup }} />
    </div>
  );
}
